import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CHARACTERS } from './constants';

const CHARACTER_SELECT_FADE_DURATION_MS = 320;
const CHARACTER_SELECT_REVEAL_DELAY_MS = 40;

const GameCharacterSelect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isBlackOverlayVisible, setIsBlackOverlayVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsVisible(true);
      setIsBlackOverlayVisible(false);
    }, CHARACTER_SELECT_REVEAL_DELAY_MS);

    return () => clearTimeout(revealTimer);
  }, []);

  const handleContinueToRules = useCallback(() => {
    if (!selectedCharacterId || isLeaving) return;

    localStorage.setItem('selectedCharacter', selectedCharacterId);
    setIsLeaving(true);
    setIsVisible(false);
    setIsBlackOverlayVisible(true);

    window.setTimeout(() => {
      navigate(`/game/rules?${searchParams.toString()}`);
    }, CHARACTER_SELECT_FADE_DURATION_MS);
  }, [isLeaving, navigate, searchParams, selectedCharacterId]);

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center overflow-hidden px-4"
      style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
    >

      <div
        className="relative w-full max-w-5xl rounded-2xl bg-[#07153a]/95 shadow-2xl border border-blue-900/40 px-3 py-2 sm:px-6 sm:py-3 md:px-8 md:py-6"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${CHARACTER_SELECT_FADE_DURATION_MS}ms ease-in-out`,
        }}
      >

        {/* Title */}
        <div className="mb-2 relative w-full">
          <div
            className="relative overflow-hidden"
            style={{
              opacity: 0.85,
              background: '#212A4D',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              borderRadius: 30,
              padding: 'clamp(12px, 2.5vw, 22px) clamp(16px, 4vw, 32px)',
            }}
          >
            <div
              className="absolute left-4 right-4 top-0 h-px"
              style={{
                background: 'linear-gradient(90deg, rgba(14.46, 133, 119.99, 0) 0%, #14B8A6 50%, rgba(8.91, 82, 73.98, 0) 100%)',
                boxShadow: '0px 4px 4px rgba(20, 184, 166, 0.40)',
              }}
            />
            <div
              className="absolute left-4 right-4 bottom-0 h-px"
              style={{
                background: 'linear-gradient(90deg, rgba(14.46, 133, 119.99, 0) 0%, #14B8A6 50%, rgba(8.91, 82, 73.98, 0) 100%)',
                boxShadow: '0px 4px 4px rgba(20, 184, 166, 0.40)',
              }}
            />
            <p
              style={{
                color: 'white',
                fontSize: 'clamp(14px, 2.4vw, 30px)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 700,
                lineHeight: 1.3,
                textAlign: 'center',
                margin: 0,
              }}
            >
              Who speaks for earth today? Select a character representing yourself.
            </p>
          </div>
        </div>

        {/* Character cards */}
        <div className="flex flex-row flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-2 md:gap-1">
          {CHARACTERS.map((character) => {
            const isSelected = selectedCharacterId === character.id;

            return (
              <button
                key={character.id}
                type="button"
                onClick={() => setSelectedCharacterId(character.id)}
                className={`group flex flex-col items-center justify-center w-[45%] sm:w-auto transition-all duration-200 ${
                  isSelected ? '-translate-y-3' : 'hover:-translate-y-1'
                }`}
              >
                <div
                  className="rounded-2xl p-2 sm:p-3 md:p-4 transition-all duration-200"
                  style={isSelected ? {
                    filter: 'drop-shadow(0 0 18px #14B8A6) drop-shadow(0 0 40px rgba(56, 189, 248, 0.1))',
                  } : undefined}
                >
                  <img
                    src={character.image}
                    alt={character.name}
                    className="h-24 sm:h-32 md:h-56 lg:h-72 w-auto object-contain"
                  />
                </div>

                <span
                  className="mt-1 sm:mt-2 text-sm sm:text-lg md:text-xl lg:text-2xl font-semibold transition"
                  style={{ fontFamily: 'Patrick Hand SC, cursive', color: '#FFE896' }}
                >
                  {character.name}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            onClick={handleContinueToRules}
            disabled={!selectedCharacterId || isLeaving}
            className="px-8 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            Continue to Rules
          </button>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-0 bg-black"
        style={{
          opacity: isBlackOverlayVisible ? 1 : 0,
          transition: `opacity ${CHARACTER_SELECT_FADE_DURATION_MS}ms ease-in-out`,
        }}
      />
    </div>
  );
};

export default GameCharacterSelect;
